import React, { useCallback, useState } from 'react';
import { Box, makeStyles } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { useOrgData } from '../../hooks/useOrgData';
import { CartItem } from '../../state/types';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { getItemFromCart, isPricingFormItem } from '../../state/utils';
import { calculateSubTotalPrice, calculateTotalPrice } from '../../utils/calculatePrice';
import { roundNumber } from '../../utils/roundPrice';
import { calculatePricingFormTotalPrice, generateDescription } from '../form/utils';
import { PlainButton } from '../PlainButton';

import { CartHeaderButton } from './CartHeaderButton';
import { CartItemDisplay } from './CartItemDisplay';
import { CartLineItem } from './CartLineItem';

type StyleProps = {
  open: boolean;
};

const useStyles = makeStyles((theme: HCPTheme) => ({
  wrapper: {
    width: '100%',
    margin: `${theme.spacing(1)}px 0`,
    marginBottom: theme.spacing(2),
    overflow: 'visible',
  },
  dropdown: {
    backgroundColor: theme.palette.background.default,
    position: 'absolute',
    left: 0,
    width: '100%',
    zIndex: 3000,
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
    paddingBottom: ({ open }: StyleProps) => (open ? theme.spacing(1) : 0),
    borderRadius: `0 0 ${theme.spacing(1)}px ${theme.spacing(1)}px`,
  },
  backdrop: {
    backgroundColor: 'rgba(0,0,0,0.1)',
    position: 'absolute',
    width: '100%',
    height: 'calc(100% - 120px)',
    zIndex: 2000,
    top: '120px',
    left: 0,
  },
}));

export const Cart = () => {
  const [open, setOpen] = useState<boolean>(false);
  const { wrapper, dropdown, backdrop } = useStyles({ open });
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { token, cart } = state;
  const { data: orgData } = useOrgData({ token });

  const clickHandle = useCallback(() => setOpen(!open), [open, setOpen]);

  const onQuantityChange = useCallback(
    (item: CartItem) => (quantity: number) => {
      dispatch(actions.changeQuantity(getItemFromCart(item).uuid, quantity));
    },
    [dispatch, actions]
  );

  const onDelete = useCallback(
    (item: CartItem) => () => {
      if (cart.length > 1) {
        dispatch(actions.removeFromCart(getItemFromCart(item).uuid));
      }
    },
    [dispatch, actions, cart]
  );

  if (cart.length === 0) {
    return <div />;
  }

  const orgTax = orgData && 'taxInfo' in orgData ? orgData?.taxInfo : null;

  const subTotal = calculateSubTotalPrice(cart);
  const total = calculateTotalPrice(cart, orgTax?.rate || null);

  const taxName = `Tax, ${orgTax?.name || 'none'} (${roundNumber((orgTax?.rate || 0) * 100)}%):`;
  const taxTotal = Math.round((total - subTotal) * 100) / 100;

  return (
    <>
      <div className={wrapper}>
        <div className={dropdown}>
          <CartHeaderButton open={open} onClick={clickHandle} />
          {open && (
            <>
              {cart.map((item) => {
                const cartItem = getItemFromCart(item);

                const props = isPricingFormItem(item)
                  ? {
                      title: cartItem.name,
                      price: calculatePricingFormTotalPrice(item.pricingForm, item.values),
                      key: cartItem.name,
                      description: generateDescription(item.pricingForm, item.values),
                      boldPrice: true,
                    }
                  : {
                      title: cartItem.name,
                      key: cartItem.uuid,
                      price: item.service.price,
                      quantity: item.quantity,
                      onQuantityChange: onQuantityChange(item),
                      onDelete: onDelete(item),
                      isLastCartItem: cart.length === 1,
                    };

                return <CartLineItem {...props} />;
              })}
              {subTotal > 0 ? (
                <Box mt={2}>
                  <CartItemDisplay title="Subtotal:" price={subTotal} />
                  <CartItemDisplay title={taxName} price={taxTotal} />
                  <CartItemDisplay bold title="Total:" price={total} />
                </Box>
              ) : null}
            </>
          )}
        </div>
      </div>
      {open && <PlainButton onClick={clickHandle} additionalClass={backdrop} />}
    </>
  );
};
